import { network } from '@root/utils'
import { handleReturn, QueryFunction } from './root'

export enum IRelationType {
  ACCRUAL_PAIR = 'ACCRUAL_PAIR',
  SALARY_PAYMENT = 'SALARY_PAYMENT'
}

export enum IRelatedDocumentRole {
  ACCRUAL = 'ACCRUAL',
  SETTLEMENT = 'SETTLEMENT'
}

export interface IRelatedDocumentSummary {
  id: number
  number: string
  date: string | null
  contact_name: string | null
  amount: number
}

/** Nested ACCRUAL_PAIR network totals (under relation_network_signals / write signals). */
export interface IAccrualPairNetworkSignals {
  due_effect: number | null
  has_due_mismatch: boolean
  invoice_total: number | null
  payment_total: number | null
}

export type IRelationNetworkSignals = {
  [IRelationType.ACCRUAL_PAIR]?: IAccrualPairNetworkSignals
  [IRelationType.SALARY_PAYMENT]?: IAccrualPairNetworkSignals
}

export interface IRelationEntrySignals {
  due_account_effect: number | null
  due_account_types: string[]
  has_ass_pay_entries: boolean
}

export interface IDocumentRelation {
  id: number
  type: IRelationType
  role: IRelatedDocumentRole
  related_document: number
  description: string
  related_document_summary?: IRelatedDocumentSummary
  /** Network signals keyed by relation_type after create/update/retrieve. */
  source_document_signals?: IRelationNetworkSignals | null
  related_document_signals?: IRelationNetworkSignals | null
}

export interface IDocumentRelationSuggestion {
  related_document: number
  role: IRelatedDocumentRole
  type: IRelationType
  reason: string
  score: number
}

interface RelationListProps {
  slug: string
  documentId: number
}

export interface IRelationSuggestionPreviewInput {
  slug: string
  date?: string | null
  contact_id?: number | null
  blueprint?: object
  exclude_document_ids?: number[]
}

export interface IRelationLinkKindMeta {
  kind: 'salesInvoice' | 'purchaseInvoice' | 'payment' | 'salary' | 'salaryPayment'
  role: IRelatedDocumentRole
  /** Present on current backends; fall back to ACCRUAL_PAIR when absent. */
  relation_type?: IRelationType
  account_types: string[]
  account_type_match: 'any' | 'all'
}

export interface IRelationSuggestionPreviewResponse {
  suggestions: IDocumentRelationSuggestion[]
  relation_entry_signals: IRelationEntrySignals
  source_amount: number | null
  available_link_kinds: IRelationLinkKindMeta[]
}

export const createDocumentRelation = async (
  { slug, documentId }: RelationListProps,
  requestData: Omit<IDocumentRelation, 'id' | 'description'>
) => {
  const url = `/v1/business/${slug}/document/${documentId}/relation/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData
  })
  return handleReturn({ status, data })
}

export const fetchDocumentRelationSuggestions: QueryFunction<
  RelationListProps,
  IDocumentRelationSuggestion[]
> = async ({ slug, documentId }: RelationListProps) => {
  const url = `/v1/business/${slug}/document/${documentId}/relation/suggestions/`
  try {
    const { data } = await network.httpClient.request({
      url,
      method: 'GET'
    })
    return data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      // Graceful fallback while backend route is unavailable.
      return []
    }
    throw error
  }
}

export const fetchDocumentRelationSuggestionPreview = async ({
  slug,
  date,
  contact_id,
  blueprint,
  exclude_document_ids = []
}: IRelationSuggestionPreviewInput): Promise<IRelationSuggestionPreviewResponse> => {
  const url = `/v1/business/${slug}/document/relation/suggestions/preview/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      date: date ?? null,
      contact_id: contact_id ?? null,
      blueprint: blueprint ?? {},
      exclude_document_ids
    }
  })
  return data
}
